import {
    Component,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import {
    AUTH_SERVICE_IMPL,
    CollectingTaskListItem,
    DeliveryTaskState,
    IAuthenticationService,
    ModalService,
    MultiPickingTask,
    UserRole,
} from 'shared';
import { PaymentType } from 'shared';
import { MyTasksService } from 'dm-src/app/modules/my-tasks/my-tasks.service';
import { Subscription } from 'rxjs';

@Component({
    selector: '[dynamic-collecting-task-item]',
    templateUrl: './dynamic-collecting-task-item.component.html',
    styleUrls: ['./dynamic-collecting-task-item.component.scss'],
})
export class DynamicCollectingTaskItemComponent implements OnInit, OnDestroy, OnChanges {
    @Input() public task: CollectingTaskListItem;
    @Input() public badgeText: string;
    @Input() public groupCollapsed = false;

    public isCollapsed = true;
    public maximumMultipickingTasks: number;
    public multiPickingSelectedTasks: MultiPickingTask[];
    public multiPickingTask: MultiPickingTask;
    public isMultiPickingSelectedTask = false;
    public multiPickingAlreadyInProgress = false;
    public PaymentType = PaymentType;
    private readonly _subscriptions = new Subscription();
    private readonly _canEditDelivererNotes: boolean;

    constructor(
        private readonly _myTasksService: MyTasksService,
        private readonly _modalService: ModalService,
        @Inject(AUTH_SERVICE_IMPL) private readonly _authService: IAuthenticationService
    ) {
        this._canEditDelivererNotes = this._authService.currentUser.hasRoles([
            UserRole.Administrator,
            UserRole.Picker,
        ]);
    }

    public ngOnInit(): void {
        this.multiPickingTask = {
            deliveryTaskID: this.task.deliveryTaskID,
            orderID: this.task.orderID,
            providerName: this.task.providerName,
            calculatedPrice: this.task.calculatedPrice,
            calculatedPriceEnabled: this.task.calculatedPriceEnabled,
            multipickingTaskIdentifierChar: this.task.multipickingTaskIdentifierChar,
            multipickingTaskIdentifierColor: this.task.multipickingTaskIdentifierColor,
        };

        this._subscriptions.add(
            this._myTasksService.maximumMultipickingTasks.subscribe((maximumTasks) => {
                this.maximumMultipickingTasks = maximumTasks;
            })
        );
        this._subscriptions.add(
            this._myTasksService.multiPickingAlreadyInProgress.subscribe((inProgress) => {
                this.multiPickingAlreadyInProgress = inProgress;
            })
        );
        this._subscriptions.add(
            this._myTasksService.selectedMultipickingTasks.subscribe((selectedTasks) => {
                this.multiPickingSelectedTasks = selectedTasks;
                this.isMultiPickingSelectedTask = selectedTasks.some(
                    (task) => task.deliveryTaskID === this.task.deliveryTaskID
                );
            })
        );
    }

    public ngOnDestroy(): void {
        this._subscriptions.unsubscribe();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.groupCollapsed?.currentValue) {
            this.isCollapsed = true;
        }
    }

    public get isDone(): boolean {
        return this.task.state === DeliveryTaskState[DeliveryTaskState.Done];
    }

    public get isDelivererNotesVisible(): boolean {
        return (
            this._canEditDelivererNotes &&
            (this.task.state === DeliveryTaskState[DeliveryTaskState.ToDo] ||
                this.task.state === DeliveryTaskState[DeliveryTaskState.InProgress])
        );
    }

    public get isMultipickingSelectionDisabled(): boolean {
        return (
            this.multiPickingAlreadyInProgress ||
            (this.multiPickingSelectedTasks?.length >= this.maximumMultipickingTasks &&
                !this.isMultiPickingSelectedTask) ||
            (this.multiPickingSelectedTasks?.length > 0 &&
                this.multiPickingSelectedTasks[0]?.providerName !==
                    this.multiPickingTask.providerName)
        );
    }

    public toggleCard(): void {
        if (!this.isDone) {
            this.isCollapsed = !this.isCollapsed;
        }
    }

    public showProductListModal(event: Event): void {
        event.stopPropagation();
        this._myTasksService.setSelectedTask(this.task);
        this._modalService.setModalVisibility(true, 'package-verify-modal');
    }

    public showCalculatedPriceInfoModal(event: Event): void {
        event.stopPropagation();
        this._modalService.setModalVisibility(true, 'calculated-price-info-modal');
    }

    public changed(event: Event): void {
        const checkbox = event.target as HTMLInputElement;

        if (
            this.multiPickingSelectedTasks?.length > 0 &&
            this.multiPickingSelectedTasks[0]?.providerName !==
                this.multiPickingTask.providerName
        ) {
            alert(
                'You can only select tasks from the same provider: ' +
                    this.multiPickingSelectedTasks[0]?.providerName
            );
            checkbox.checked = false;
            return;
        }

        if (
            this.multiPickingSelectedTasks?.length >= this.maximumMultipickingTasks &&
            checkbox.checked
        ) {
            alert('The selectable tasks are maximum ' + this.maximumMultipickingTasks);
            checkbox.checked = false;
            return;
        }

        checkbox.checked
            ? this._myTasksService.selectMultipickingTask(this.multiPickingTask)
            : this._myTasksService.deselectMultipickingTask(this.multiPickingTask);
    }
}
